import { useRef, useState } from "react";

/**
 * useNewsletterSubscribe — shared state machine + submit logic.
 *
 * Wire protocol (POST /api/newsletter-subscribe.php JSON):
 *   { email: string, consent: true, website: "" , startedAt: number }
 *
 *   • website — hidden honeypot field. Must remain empty on real submissions.
 *   • startedAt — epoch ms of the moment the user first interacted with the
 *     form; the PHP endpoint rejects submissions that arrive unrealistically
 *     fast (bot-like behaviour).
 *
 * The hook never logs the subscriber's email to the console.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ERROR_MESSAGES = {
  invalidEmail: "Enter a valid work email address.",
  server: "We could not process your request. Please try again shortly.",
};

export const useNewsletterSubscribe = () => {
  const [state, setState] = useState("default"); // default | sending | success | error
  const [error, setError] = useState(null);
  const startedAt = useRef(Date.now());
  const pending = useRef(false); // synchronous guard so double-clicks in one tick can't fire twice

  const reset = () => {
    setState("default");
    setError(null);
    startedAt.current = Date.now();
  };

  const submit = async ({ email, website }) => {
    // Synchronous guard: React state updates are async, so two clicks in the
    // same event-loop tick would both observe state='default'. A useRef flag
    // gives us an atomic block on duplicate submissions while pending.
    if (pending.current || state === "sending") return { ok: false };

    if (!email || !EMAIL_RE.test(email.trim())) {
      setState("error");
      setError(ERROR_MESSAGES.invalidEmail);
      return { ok: false };
    }
    pending.current = true;
    setState("sending");
    setError(null);

    try {
      const resp = await fetch("/api/newsletter-subscribe.php", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          consent: true,
          website: website || "",
          startedAt: startedAt.current,
        }),
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      let data = null;
      try {
        data = await resp.json();
      } catch {
        // Empty body but 2xx is acceptable.
      }
      if (data && data.ok === false) {
        setState("error");
        setError(
          data.message === "invalid_email"
            ? ERROR_MESSAGES.invalidEmail
            : ERROR_MESSAGES.server
        );
        return { ok: false };
      }

      setState("success");
      return { ok: true };
    } catch {
      setState("error");
      setError(ERROR_MESSAGES.server);
      return { ok: false };
    } finally {
      pending.current = false;
    }
  };

  return { state, error, submit, reset };
};
